import React, { useState, useRef, useCallback, useEffect } from 'react'
import styles from './styles.module.scss'
import { useTranslation } from 'react-i18next'
import Icon from 'srcReact/components/icons/Icon'
import Button from '../Button'
import { decode } from 'html-entities/lib/index'
import { getCroppedImg } from '../CropAndUploadPicture/utils'
import Cropper from 'react-easy-crop'
import { useSelector, useDispatch } from 'react-redux'
import { commonActions } from 'srcReact/components'
import ConfirmModal from 'srcReact/components/common2/ConfirmModal'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import { getProcessFiles } from 'srcReact/helpers/fileHelper'
import { redirectToPage } from 'srcReact/helpers/history'
import useInterval from 'srcReact/helpers/hooks/useInterval'

export default function ImageCropper({
  containerCSS,
  buttonWrapperCSS,
  editMode,
  handleSave,
  handleSkip,
  handleNext,
  aspectRatio,
  hasRoundShape = false,
  text,
  exisingImageUrl,
  entityUpdated = false,
  entityUpdating = false,
  buttonWrapperSize,
  showNextButton = true,
  isLoggedIn = 1,
  skipText = '',
  recommendedFormatText = '',
  showUploadZone = false,
  hideNextButton = false,
  hideSkipButton = false,
  externallyUploadedFile = null,
  handleCancel,
  allowDelete = true,
  actionButtonCSS = '',
  actionWrapperCSS = '',
  cropContainerCSS = '',
  handleError,
  apiSubDomainData = {},
  cropSize,
  callbackOnImageUploadStart //this function is called once user clicks on confirm
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const fileInputRef = useRef()
  const [fileError, setFileError] = useState('')
  const [rotate, setRotate] = useState(0)
  const [scale, setScale] = useState(1)
  const [image, setImage] = useState(null)
  const [croppedArea, setCroppedArea] = useState(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [croppedImageUrl, setCroppedImageUrl] = useState('')
  const [croppedImage, setCroppedImage] = useState(null)
  const acceptedFileTypes = appConstantSettings.CROPPER_ACCEPTED_IMAGE_TYPES
  const [originalFileMeta, setOriginalFileMeta] = useState({})
  const [pictureUpdateCalled, setPictureUpdateCalled] = useState(false)
  const [isDeleteImageModalOpen, setIsDeleteImageModalOpen] = useState(false)
  const {
    common: {
      isNative,
      fileCreation: { files: uploadedFiles, loadingFile, error }
    }
  } = useSelector((state) => state)

  useEffect(() => {
    if (isDeleteImageModalOpen && entityUpdated && !entityUpdating) {
      closeConfirmDeleteImageModal()
    }
  }, [entityUpdated, entityUpdating])

  useEffect(() => {
    if (exisingImageUrl && exisingImageUrl.length > 0) {
      setCroppedImageUrl(exisingImageUrl)
    }
  }, [exisingImageUrl])

  useEffect(() => {
    if (externallyUploadedFile !== null) {
      processSelectedFile(externallyUploadedFile)
    }
  }, [externallyUploadedFile])

  useEffect(() => {
    if (handleError && fileError) {
      handleError(fileError)
    }
  }, [fileError])

  useEffect(() => {
    if (!loadingFile && uploadedFiles?.length > 0 && croppedImage) {
      fileInputRef.current.value = null
      if (editMode && handleSave) {
        handleSaveImage()
      } else if (handleNext) {
        handleNext(uploadedFiles[0])
      }
    }
  }, [JSON.stringify(uploadedFiles)])

  const processSelectedFile = async (file) => {
    setScale(1)
    setRotate(0)
    setFileError('')
    setImage(null)
    setCroppedImageUrl('')
    setCroppedImage(null)
    if (!validateFile(file)) {
      if (handleCancel) {
        handleCancel()
      }
      return false
    }
    setOriginalFileMeta({
      type: file.type,
      size: file.size,
      name: file.name
    })

    const imageData = await readFile(file)
    setImage(imageData)

    // try {
    //   const imageData = await readFile(file)
    //   setImage(imageData)
    // } catch (error) {
    //   setFileError(error)
    // }
  }

  useInterval(() => {
    const processedCroppedImage = JSON.parse(localStorage.getItem('files_creation'))
    if (processedCroppedImage) {
      setImage(processedCroppedImage[0])
      setCroppedImage(processedCroppedImage[0])
      setCroppedImageUrl(processedCroppedImage[0]?.url)
      setPictureUpdateCalled(true)
      if (callbackOnImageUploadStart) {
        callbackOnImageUploadStart()
      }
      dispatch(commonActions.startStopFileProcessing('start'))
      dispatch(
        commonActions.fileCreation([processedCroppedImage[0]], {
          type: 'picture',
          is_logged: isLoggedIn,
          ...apiSubDomainData
        })
      )
      localStorage.removeItem('files_creation')
    }
  }, appConstantSettings.NATIVE_PICTURE_UPDATE_INTERVAL)

  const onSelectFile = async (e, drop = false) => {
    const files = drop ? e.dataTransfer.files : e.target.files
    const file = files[0]
    if (!file) return false
    processSelectedFile(file)
  }

  // Function to validate image dimensions
  // const validateImageDimensions = (croppedAreaPixels) => {
  //   const { width, height } = croppedAreaPixels
  //   console.log('width::', width, recommendedImageWidth, width < recommendedImageWidth)
  //   console.log('height::', height, recommendedImageHeight, height < recommendedImageHeight)
  //   // // Check if width and height meet recommended resolution
  //   // if (width < recommendedImageWidth || height < recommendedImageHeight) {
  //   //   console.log('Image resolution is below recommended size. Please select a higher resolution image.')
  //   //   setFileError('Image resolution is below recommended size. Please select a higher resolution image.')
  //   //   return false
  //   // }

  //   // Check if aspect ratio matches recommended aspect ratio
  //   const croppedAspectRatio = width / height
  //   console.log('croppedAspectRatio::', croppedAspectRatio, aspectRatio)
  //   if (croppedAspectRatio < aspectRatio) {
  //     setFileError(
  //       'Image aspect ratio does not match recommended aspect ratio. Please select an image with the correct aspect ratio.'
  //     )
  //     return false
  //   } else {
  //     setFileError('')
  //   }

  //   return true
  // }

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    //if (!validateImageDimensions(croppedAreaPixels)) {
    // Reset crop state if image dimensions are invalid
    setCroppedArea(croppedAreaPixels)
    // }
  }, [])

  const validateFile = (file) => {
    const acceptedFileTypesArray = acceptedFileTypes.split(',').map((item) => item.trim())
    if (!acceptedFileTypesArray.includes(file.type)) {
      setFileError(t('group.createGroup.groupPicture.invalidFileType'))
      return false
    }
    const fileSizeInMB = file.size / 1024 / 1024 // in MiB
    if (fileSizeInMB > appConstantSettings.IMAGE_CROPPER_FILE_SIZE_MB) {
      setFileError(
        t('group.createGroup.groupPicture.invalidFileSize', {
          size: `${appConstantSettings.IMAGE_CROPPER_FILE_SIZE_MB}MB`
        })
      )
      return false
    }
    return true
  }
  const readFile = useCallback((file) => {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)

        // reader.onload = (event) => {
        //   const img = new Image()
        //   img.onload = function () {
        //     const width = this.width
        //     const height = this.height
        //     console.log('images aspect ratio::', width, height, width / height)
        //     resolve(reader.result)
        //     // if (width > recommendedImageWidth && height > recommendedImageHeight) {
        //     //   // Image meets recommended size
        //     //   resolve(reader.result)
        //     // } else {
        //     //   // Image does not meet recommended size
        //     //   reject(new Error('Selected image does not meet recommended size.'))
        //     // }
        //   }
        //   img.src = event.target.result
        // }

        reader.readAsDataURL(file)
      } catch (error) {
        reject(error)
      }
    })
  }, [])

  const onDragOver = (e) => {
    e.preventDefault()
  }
  const onDragLeave = (e) => {
    e.preventDefault()
  }
  const onDrop = (e) => {
    e.preventDefault()
    onSelectFile(e, true)
  }

  const handleRotate = (e) => {
    e.preventDefault()
    if (rotate === 0) setRotate(90)
    else if (rotate === 90) setRotate(180)
    else if (rotate === 180) setRotate(270)
    else setRotate(0)
  }

  const handleScale = (e, type) => {
    e.preventDefault()
    if (type === 'in') {
      const updatedScale = scale + 0.1
      if (updatedScale <= 1.5) {
        setScale(updatedScale)
      }
    } else if (type === 'out') {
      const updatedScale = scale - 0.1
      if (updatedScale >= -0.5) {
        setScale(updatedScale)
      }
    } else {
      setScale(1)
    }
  }

  const handleSaveImage = () => {
    if (uploadedFiles?.length > 0 && croppedImageUrl && croppedImageUrl.length > 0) {
      const file = uploadedFiles[0]
      handleSave({
        id: file.id,
        filename: file.filename,
        mimeType: file.mimeType
      })
    } else {
      handleSave({
        id: 0,
        filename: '',
        mimeType: ''
      })
    }
  }

  useEffect(() => {
    if (!loadingFile && pictureUpdateCalled) {
      if (!showNextButton) {
        handleSkip()
      }
    }
  }, [loadingFile, pictureUpdateCalled])

  const showCroppedImage = useCallback(async () => {
    try {
      const { croppedImage, croppedImageUrl } = await getCroppedImg(image, croppedArea, originalFileMeta, rotate)
      setCroppedImage(croppedImage)
      setCroppedImageUrl(croppedImageUrl)
      setPictureUpdateCalled(true)
      if (callbackOnImageUploadStart) {
        callbackOnImageUploadStart()
      }
      dispatch(commonActions.startStopFileProcessing('start'))
      const processedCroppedImage = await getProcessFiles([croppedImage])
      if (processedCroppedImage?.length > 0) {
        dispatch(
          commonActions.fileCreation([processedCroppedImage[0]], {
            type: 'picture',
            is_logged: isLoggedIn,
            ...apiSubDomainData
          })
        )
      }
    } catch (e) {
      dispatch(commonActions.startStopFileProcessing('stop'))
      console.error(e)
    }
  }, [image, croppedArea, rotate])

  const removePicture = () => {
    if (editMode && exisingImageUrl) {
      openConfirmDeleteImageModal()
    } else {
      setImage(null)
      setCroppedImage(null)
      setCroppedImageUrl('')
      setCroppedArea(null)
      setRotate(0)
      setScale(1)
      fileInputRef.current.value = ''
      dispatch(commonActions.fileCreationRemove)
    }
  }

  const changeCoverPicture = () => {
    if (isNative) {
      redirectToPage('/cover/picture/upload', isNative)
      return
    }
    fileInputRef.current.click()
  }

  const deleteCoverPicture = () => {
    setImage(null)
    setCroppedImage(null)
    setCroppedImageUrl('')
    setCroppedArea(null)
    setRotate(0)
    setScale(1)
    handleSave({
      id: 0,
      filename: '',
      mimeType: ''
    })
  }

  const openConfirmDeleteImageModal = () => {
    setIsDeleteImageModalOpen(true)
  }
  const closeConfirmDeleteImageModal = () => {
    setIsDeleteImageModalOpen(false)
  }

  const cancelUpdatingPicture = () => {
    setImage(null)
    setCroppedImage(null)
    setCroppedArea(null)
    setRotate(0)
    setScale(1)
    fileInputRef.current.value = ''
    setCroppedImageUrl(exisingImageUrl && exisingImageUrl.length > 0 ? `${exisingImageUrl}` : '')
    if (handleCancel) {
      handleCancel()
    }
  }
  const handleCancelEdit = () => {
    setImage(null)
    setCroppedImage(null)
    setCroppedImageUrl(exisingImageUrl)
    setCroppedArea(null)
    setRotate(0)
    setScale(1)
    fileInputRef.current.value = ''
  }
  return (
    <div className={`${styles.content} ${containerCSS}`}>
      <input
        ref={fileInputRef}
        className={styles.fileInput}
        accept={appConstantSettings.CROPPER_ACCEPTED_IMAGE_TYPES}
        type='file'
        onChange={onSelectFile}
      />
      {image !== null ? (
        croppedImageUrl ? (
          <React.Fragment>
            <div className={styles.croppedImageContainer}>
              {uploadedFiles?.length > 0 && uploadedFiles[0]?.url?.length > 0 ? (
                <img alt={t('group.createGroup.groupPicture.alt')} src={uploadedFiles[0].url} />
              ) : entityUpdated && exisingImageUrl ? (
                <img alt={t('group.createGroup.groupPicture.alt')} src={exisingImageUrl} />
              ) : (
                <span className={styles.uploadLoading}>
                  {error ? t('global.imageError') : t('global.uploadingImage')}
                </span>
              )}
              {/* <img alt={t('group.createGroup.groupPicture.alt')} src={croppedImageUrl} /> */}

              <div className={styles.layer} />
              {text && <div className={styles.textWithCropped}>{decode(text)}</div>}
              {editMode ? (
                <div className={styles.actionWrapperEditMode}>
                  <button
                    type='button'
                    title={t('group.createGroup.groupPicture.reuploadInfo')}
                    className={`${styles.iconButton} ${actionButtonCSS}`}
                    onClick={() => fileInputRef.current.click()}>
                    <Icon name='upload' styleModifier={styles.icon} />
                  </button>
                  {allowDelete && (
                    <button
                      type='button'
                      title={t('group.createGroup.groupPicture.deleteInfo')}
                      className={`${styles.iconButton} ${actionButtonCSS}`}
                      onClick={removePicture}>
                      <Icon name='delete' styleModifier={styles.icon} />
                    </button>
                  )}
                </div>
              ) : (
                <div className={styles.actionWrapperEditMode}>
                  <button
                    type='button'
                    title={t('group.createGroup.groupPicture.reuploadInfo')}
                    className={`${styles.iconButton} ${styles.uploadBtnStandalone} ${actionButtonCSS}`}
                    onClick={() => {
                      handleCancelEdit()
                      dispatch(commonActions.fileCreationRemove())
                    }}>
                    <Icon name='delete' styleModifier={styles.icon} />
                  </button>
                </div>
              )}
            </div>
          </React.Fragment>
        ) : (
          <div
            className={`${cropContainerCSS ? cropContainerCSS : styles.easyCropContainer}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}>
            <Cropper
              restrictPosition={false}
              image={image}
              crop={crop}
              zoom={scale}
              aspect={aspectRatio} //{isMobile ? 10 / 4 : 19 / 4}
              showGrid={false}
              rotation={rotate}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              objectFit='horizontal-cover'
              cropShape={hasRoundShape ? 'round' : 'rect'}
              cropSize={cropSize}
            />
            <div className={`${styles.actionWrapper} ${actionWrapperCSS}`}>
              <button
                type='button'
                title={t('group.createGroup.groupPicture.rotateInfo')}
                className={`${styles.iconButton} ${actionButtonCSS}`}
                onClick={handleRotate}>
                <Icon name='rotate' styleModifier={styles.icon} />
              </button>
              <button
                type='button'
                title={t('group.createGroup.groupPicture.zoomInInfo')}
                className={`${styles.iconButton} ${actionButtonCSS}`}
                onClick={(e) => handleScale(e, 'in')}>
                <Icon name='zoom-in' styleModifier={styles.icon} />
              </button>
              <button
                type='button'
                title={t('group.createGroup.groupPicture.zoomOutInfo')}
                className={`${styles.iconButton} ${actionButtonCSS}`}
                onClick={(e) => handleScale(e, 'out')}>
                <Icon name='zoom-out' styleModifier={styles.icon} />
              </button>
              <button
                type='button'
                title={t('group.createGroup.groupPicture.reuploadInfo')}
                className={`${styles.iconButton} ${actionButtonCSS}`}
                onClick={() => fileInputRef.current.click()}>
                <Icon name='upload' styleModifier={styles.icon} />
              </button>
              {!editMode && showUploadZone && (
                <button
                  type='button'
                  title={t('group.createGroup.groupPicture.deleteInfo')}
                  className={`${styles.iconButton} ${actionButtonCSS}`}
                  onClick={removePicture}>
                  <Icon name='delete' styleModifier={styles.icon} />
                </button>
              )}
            </div>
            {text && <div className={styles.text}>{text}</div>}
          </div>
        )
      ) : editMode && croppedImageUrl ? (
        <React.Fragment>
          <div className={styles.croppedImageContainer}>
            <img alt={t('group.createGroup.groupPicture.alt')} src={croppedImageUrl} />
            <div className={styles.layer} />
            {text && <div className={styles.textWithCropped}>{decode(text)}</div>}

            <div className={styles.actionWrapperEditMode}>
              <button
                type='button'
                title={t('group.createGroup.groupPicture.reuploadInfo')}
                className={`${styles.iconButton} ${actionButtonCSS}`}
                onClick={changeCoverPicture}>
                <Icon name='upload' styleModifier={styles.icon} />
              </button>
              {allowDelete && (
                <button
                  type='button'
                  title={t('group.createGroup.groupPicture.deleteInfo')}
                  className={`${styles.iconButton} ${actionButtonCSS}`}
                  onClick={removePicture}>
                  <Icon name='delete' styleModifier={styles.icon} />
                </button>
              )}
            </div>
          </div>
        </React.Fragment>
      ) : showUploadZone ? (
        <div className={`${styles.customDropzone}`} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
          <div className={styles.uploadWrapper}>
            <Button
              handleClick={changeCoverPicture}
              text={t('global.upload')}
              size={buttonWrapperSize ? buttonWrapperSize : 'medium'}
              type='button'
              customStyle={styles.uploadButton}
            />
          </div>
        </div>
      ) : null}
      {fileError && <div className={styles.fileError}>{fileError}</div>}
      <span className={styles.recommendedFormatInfo}>
        {recommendedFormatText || t('group.createGroup.groupPicture.recommendedFormat')}
      </span>

      <div className={`${styles.buttonWrapper} ${buttonWrapperCSS}`}>
        {image !== null && croppedImage === null ? (
          <React.Fragment>
            {editMode && (
              <Button
                type='button'
                style='secondary'
                size={buttonWrapperSize ? buttonWrapperSize : 'large'}
                text={t('group.createGroup.btnCancel')}
                handleClick={editMode ? cancelUpdatingPicture : handleCancelEdit}
              />
            )}
            {!showNextButton && !editMode && (
              <Button
                type='button'
                style='secondary'
                size={buttonWrapperSize ? buttonWrapperSize : 'large'}
                text={t('global.cancel')}
                handleClick={handleSkip}
              />
            )}
            <Button
              type='button'
              style='primary'
              size={buttonWrapperSize ? buttonWrapperSize : 'large'}
              text={t('group.createGroup.groupPicture.btnConfirm')}
              handleClick={showCroppedImage}
            />
          </React.Fragment>
        ) : croppedImage === null ? (
          !editMode &&
          showUploadZone &&
          !hideNextButton &&
          !hideSkipButton && (
            <Button
              type='button'
              style='secondary'
              size={buttonWrapperSize ? buttonWrapperSize : 'large'}
              text={skipText.length > 0 ? skipText : t('global.skip')}
              handleClick={handleSkip}
              customStyle={styles.skipButton}
            />
          )
        ) : null}
      </div>
      {isDeleteImageModalOpen && (
        <ConfirmModal
          isOpen={isDeleteImageModalOpen}
          loading={entityUpdating}
          title={t('group.createGroup.groupPicture.confirmDeleteImage')}
          handlePrimaryButton={deleteCoverPicture}
          handleSecondaryButton={closeConfirmDeleteImageModal}
          closeModal={closeConfirmDeleteImageModal}
        />
      )}
    </div>
  )
}
